import React, { useState } from 'react';
import { usePropertyDefs } from '../../../contexts/PropertyDefsContext';
import {
	PROPERTY_TYPES,
	TYPE_META,
	slotUsage,
	addProperty,
	updateProperty,
	archiveProperty,
	restoreProperty,
} from '../../../services/propertyDefsModel';

/* Manage a space's user-defined properties.

   Every mutator in propertyDefsModel returns `{ defs, error }` and never throws,
   because each failure here is something the user needs told — "that name is
   taken", "all five selects are in use" — rather than a bug. This component's
   whole job is to run one, show the error if there is one, and persist the new
   list if there isn't.

   DELETE IS ARCHIVE, deliberately, and the copy says so. Archiving keeps the
   values AND keeps the slot: a property can be brought back with its data
   intact, but its slot is not handed to a new property, because a fresh "Cost"
   inheriting numbers from a long-archived "Budget" is a silent corruption
   nobody would catch. Genuinely reclaiming a slot needs the field cleared off
   every task first, which is a real data operation and deliberately not a
   button. */
function PropertyEditor({ onClose }) {
	const { defs, canEdit, save, error: loadError } = usePropertyDefs();
	const [label, setLabel] = useState('');
	const [type, setType] = useState('select');
	const [optionsText, setOptionsText] = useState('');
	const [error, setError] = useState(null);
	const [busy, setBusy] = useState(false);
	const [showArchived, setShowArchived] = useState(false);

	const usage = slotUsage(defs);
	const active = defs.filter((d) => !d.archived);
	const archived = defs.filter((d) => d.archived);

	const apply = async (result) => {
		if (result.error) {
			setError(result.error);
			return false;
		}
		setError(null);
		setBusy(true);
		try {
			await save(result.defs);
			return true;
		} catch (e) {
			setError(e?.message || 'Could not save.');
			return false;
		} finally {
			setBusy(false);
		}
	};

	const onAdd = async (e) => {
		e.preventDefault();
		const options = optionsText
			.split(',')
			.map((s) => s.trim())
			.filter(Boolean);
		const ok = await apply(addProperty(defs, { label, type, options }));
		if (ok) {
			setLabel('');
			setOptionsText('');
		}
	};

	return (
		/* `.wt-overlay` + `.wt-sheet`, matching TaskDetail exactly. NOT
		   `.wt-sheet-overlay`, which is the module's MOBILE filter sheet and
		   carries `display: none` above 768px — borrowing it made this editor
		   invisible on desktop while every test passed. */
		<div
			className='wt-overlay'
			role='dialog'
			aria-label='Manage properties'
			onClick={onClose}>
			<div
				className='wt-sheet wt-sheet--props'
				onClick={(e) => e.stopPropagation()}>
				<div className='wt-sheet__bar'>
					<span className='wt-sheet__eyebrow'>Properties</span>
					<button
						type='button'
						className='wt-iconbtn'
						aria-label='Close'
						onClick={onClose}>
						<i className='fa-sharp fa-solid fa-xmark' />
					</button>
				</div>

				<div className='wt-sheet__body'>
					{!canEdit && (
						<p className='wt-pe__note'>
							This space doesn't have a property schema.
						</p>
					)}
					{loadError && (
						<p className='wt-pe__error'>Couldn't load the current properties.</p>
					)}

					{active.length > 0 && (
						<ul className='wt-pe__list'>
							{active.map((d) => (
								<li className='wt-pe__item' key={d.slot}>
									<i
										className={`fa-sharp fa-solid ${TYPE_META[d.type].icon} wt-pe__icon`}
									/>
									<input
										className='wt-pe__name'
										defaultValue={d.label}
										aria-label={`Rename ${d.label}`}
										onBlur={(e) => {
											if (e.target.value.trim() === d.label) return;
											apply(updateProperty(defs, d.slot, { label: e.target.value }));
										}}
									/>
									<span className='wt-pe__type'>{TYPE_META[d.type].label}</span>
									{TYPE_META[d.type].hasOptions && (
										<input
											className='wt-pe__options'
											defaultValue={d.options.join(', ')}
											aria-label={`Options for ${d.label}`}
											placeholder='Options, comma separated'
											onBlur={(e) =>
												apply(
													updateProperty(defs, d.slot, {
														options: e.target.value.split(',').map((s) => s.trim()),
													})
												)
											}
										/>
									)}
									<button
										type='button'
										className='btn-cyv-ghost wt-pe__btn'
										disabled={busy}
										onClick={() => apply(archiveProperty(defs, d.slot))}>
										Archive
									</button>
								</li>
							))}
						</ul>
					)}

					{active.length === 0 && canEdit && (
						<p className='wt-pe__note'>
							No properties yet. Add one below and it'll appear on every task in
							this space.
						</p>
					)}

					{canEdit && (
						<form className='wt-pe__add' onSubmit={onAdd}>
							<input
								className='wt-pe__name'
								placeholder='Property name'
								value={label}
								aria-label='New property name'
								onChange={(e) => setLabel(e.target.value)}
							/>
							<select
								className='wt-pe__type-select'
								value={type}
								aria-label='Property type'
								onChange={(e) => setType(e.target.value)}>
								{PROPERTY_TYPES.map((t) => (
									<option key={t} value={t} disabled={usage[t].free === 0}>
										{TYPE_META[t].label} ({usage[t].free} left)
									</option>
								))}
							</select>
							{TYPE_META[type].hasOptions && (
								<input
									className='wt-pe__options'
									placeholder='Options, comma separated'
									value={optionsText}
									aria-label='Options'
									onChange={(e) => setOptionsText(e.target.value)}
								/>
							)}
							<button type='submit' className='btn-cyv-teal wt-pe__btn' disabled={busy}>
								Add
							</button>
						</form>
					)}

					{error && <p className='wt-pe__error'>{error}</p>}

					{archived.length > 0 && (
						<div className='wt-pe__archived'>
							<button
								type='button'
								className='btn-cyv-ghost wt-pe__btn'
								onClick={() => setShowArchived((v) => !v)}>
								{showArchived ? 'Hide' : 'Show'} archived ({archived.length})
							</button>
							{showArchived && (
								<ul className='wt-pe__list'>
									{archived.map((d) => (
										<li className='wt-pe__item is-archived' key={d.slot}>
											<span className='wt-pe__name-static'>{d.label}</span>
											<span className='wt-pe__type'>{TYPE_META[d.type].label}</span>
											<button
												type='button'
												className='btn-cyv-ghost wt-pe__btn'
												disabled={busy}
												onClick={() => apply(restoreProperty(defs, d.slot))}>
												Restore
											</button>
										</li>
									))}
								</ul>
							)}
							<p className='wt-pe__note'>
								Archived properties keep their values and their slot, so restoring
								one brings its data back.
							</p>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}

export default PropertyEditor;
